/**
 * eformApprovalLabels
 *
 * 統一處理 eForm 的「批准 / 拒絕」按鈕（以及狀態文字）顯示名稱。
 * 設計目標：
 *   - 流程設計者可在 sendEForm 節點設置自訂的 Approve / Reject 顯示名稱
 *     （例如：通過 / 不通過、WON / LOST），讓表單界面、待辦清單、流程監控
 *     等所有相關介面都按該設置顯示。
 *   - 若沒有自訂設置，則回退到原本的 i18n 翻譯字串。
 *
 * 來源優先順序（依序檢查，取第一個非空字串）：
 *   1. eform.approvalLabels.approve / reject  （後端 BuildApprovalLabels 的輸出）
 *   2. eform.approveButtonLabel / rejectButtonLabel  （直接欄位，camelCase）
 *   3. eform.ApproveButtonLabel / RejectButtonLabel  （PascalCase 後備）
 *   4. fallbackKeys 的 i18n 翻譯
 */

const DEFAULT_FALLBACK_KEYS = {
  approve: 'pendingTasks.approve',
  reject: 'pendingTasks.reject',
};

const trimToNonEmpty = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : '';
};

const pickCustomLabel = (eform, primaryKey, secondaryKey) => {
  if (!eform || typeof eform !== 'object') return '';

  const fromGroup = trimToNonEmpty(eform.approvalLabels?.[primaryKey]);
  if (fromGroup) return fromGroup;

  const fromCamelCase = trimToNonEmpty(eform[secondaryKey]);
  if (fromCamelCase) return fromCamelCase;

  const pascalKey = secondaryKey.charAt(0).toUpperCase() + secondaryKey.slice(1);
  const fromPascalCase = trimToNonEmpty(eform[pascalKey]);
  if (fromPascalCase) return fromPascalCase;

  return '';
};

/**
 * 取得 eForm 的 approve / reject 顯示文字。
 *
 * @param {object|null|undefined} eform 表單實例物件（可包含 approveButtonLabel /
 *        rejectButtonLabel / approvalLabels 欄位），允許 null / undefined。
 * @param {function} t 翻譯函式（來自 useLanguage）。
 * @param {{ approve?: string, reject?: string }} [fallbackKeys] 當 eform
 *        沒有自訂名稱時，要使用的 i18n key。預設為 pendingTasks.approve /
 *        pendingTasks.reject。
 * @returns {{ approve: string, reject: string, hasCustomApprove: boolean,
 *             hasCustomReject: boolean }}
 */
export const getEFormApprovalLabels = (eform, t, fallbackKeys) => {
  const keys = {
    approve: fallbackKeys?.approve || DEFAULT_FALLBACK_KEYS.approve,
    reject: fallbackKeys?.reject || DEFAULT_FALLBACK_KEYS.reject,
  };

  const customApprove = pickCustomLabel(eform, 'approve', 'approveButtonLabel');
  const customReject = pickCustomLabel(eform, 'reject', 'rejectButtonLabel');

  const safeT = typeof t === 'function' ? t : (key) => key;

  return {
    approve: customApprove || safeT(keys.approve),
    reject: customReject || safeT(keys.reject),
    hasCustomApprove: Boolean(customApprove),
    hasCustomReject: Boolean(customReject),
  };
};

export default getEFormApprovalLabels;
